/**
 * PlaySnowman.com — Cart Abandonment Recovery Widget
 * "A snowman is an eight."
 *
 * Paste this into your Shopify theme's assets/ folder as cart-recovery.js
 * Then include in theme.liquid: <script src="{{ 'cart-recovery.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  /* ── CONFIGURATION ─────────────────────────────────────────── */
  const CONFIG = {
    // Discount code shown to recovering customers
    discountCode: 'SNOWMAN10',
    discountPercent: 10,

    // Timer: ms before showing recovery (5 minutes = 300000ms)
    abandonThresholdMs: 300000,
    demoSpeedMs: 5000, // used by demo page only

    // How often to poll the cart (ms)
    pollIntervalMs: 8000,

    // LocalStorage keys
    lsKeyCartTimestamp: 'ps_cart_recovery_timestamp',
    lsKeyDismissed: 'ps_cart_recovery_dismissed',
    lsKeyDemoCart: 'ps_demo_cart',

    // Cross-sell mapping: if cart contains KEY, suggest VALUE
    crossSells: {
      polo:       { name: 'Snowman Rope Hat',      price: '$32', emoji: '🧢', handle: 'snowman-rope-hat' },
      tee:        { name: 'Snowman Caddy Towel',   price: '$24', emoji: '🧖', handle: 'snowman-caddy-towel' },
      headcover:  { name: 'Complete the Look — Polo + Hat', price: '$77', emoji: '⛳', handle: 'snowman-polo' },
      hoodie:     { name: 'Snowman Rope Hat',      price: '$32', emoji: '🧢', handle: 'snowman-rope-hat' },
      hat:        { name: 'Snowman Caddy Towel',   price: '$24', emoji: '🧖', handle: 'snowman-caddy-towel' },
      towel:      { name: 'Snowman Tee',           price: '$29', emoji: '👕', handle: 'snowman-tee' },
    },

    // Fallback cross-sell if no specific mapping
    fallback: { name: 'Snowman Rope Hat', price: '$32', emoji: '🧢', handle: 'snowman-rope-hat' },
  };

  /* ── STATE ─────────────────────────────────────────────────── */
  let timerId = null;
  let pollId = null;
  let isShowing = false;
  let hasCheckout = false;

  /* ── DOM UTILS ─────────────────────────────────────────────── */
  function el(tag, cls, html) {
    const e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html) e.innerHTML = html;
    return e;
  }

  function onClickOutside(widget, overlay, handler) {
    function onDocClick(e) {
      if (!widget.contains(e.target)) {
        handler();
        document.removeEventListener('click', onDocClick);
      }
    }
    // delay so the opening click doesn't immediately close
    setTimeout(() => document.addEventListener('click', onDocClick), 200);
  }

  /* ── CART API ──────────────────────────────────────────────── */
  // In production, reads Shopify cart. On the demo page, falls back to localStorage mock.
  async function fetchCart() {
    // Demo mode override
    const demo = localStorage.getItem(CONFIG.lsKeyDemoCart);
    if (demo) {
      try { return JSON.parse(demo); } catch (_) { /* continue */ }
    }
    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!res.ok) throw new Error('cart fetch failed');
      return await res.json();
    } catch (err) {
      // Graceful degradation: if fetch fails (e.g. offline or non-Shopify host), return empty
      return { item_count: 0, items: [] };
    }
  }

  /* ── CROSS-SELL LOGIC ──────────────────────────────────────── */
  function getCrossSell(cart) {
    if (!cart.items || !cart.items.length) return CONFIG.fallback;

    // Build a lowercase set of product types/handles in cart
    const handles = new Set(cart.items.map(i => (i.handle || '').toLowerCase()));
    const titles = cart.items.map(i => (i.product_title || i.title || '').toLowerCase());

    // Priority order: check each mapping
    for (const key of Object.keys(CONFIG.crossSells)) {
      const inCart = handles.has(key) || titles.some(t => t.includes(key));
      if (inCart) {
        // Don't suggest something already in cart
        const suggestion = CONFIG.crossSells[key];
        const suggestedHandle = suggestion.handle.toLowerCase();
        if (!handles.has(suggestedHandle) && !titles.some(t => t.includes(suggestedHandle))) {
          return suggestion;
        }
      }
    }
    return CONFIG.fallback;
  }

  function getPrimaryProductName(cart) {
    if (!cart.items || !cart.items.length) return 'this item';
    const first = cart.items[0];
    return first.product_title || first.title || 'this item';
  }

  /* ── WIDGET RENDER ─────────────────────────────────────────── */
  function buildWidget(cart) {
    const cross = getCrossSell(cart);
    const productName = getPrimaryProductName(cart);

    const overlay = el('div', 'ps-cr-overlay');
    const widget = el('div', 'ps-cr-widget');

    widget.innerHTML = `
      <button class="ps-cr-close" aria-label="Close">×</button>
      <div class="ps-cr-emoji">⛳</div>
      <p class="ps-cr-headline">Still thinking about the ${escapeHtml(productName)}?</p>
      <p class="ps-cr-sub">Golfers who bought this also grabbed the <strong>${escapeHtml(cross.name)}</strong>.</p>
      <div class="ps-cr-crossell">
        <div class="ps-cr-crossell-icon">${cross.emoji}</div>
        <div class="ps-cr-crossell-info">
          <div class="ps-cr-crossell-label">Complete the look</div>
          <div class="ps-cr-crossell-name">${escapeHtml(cross.name)}</div>
          <div class="ps-cr-crossell-price">${escapeHtml(cross.price)}</div>
        </div>
      </div>
      <a class="ps-cr-cta" href="/checkout?discount=${CONFIG.discountCode}">
        Complete Checkout — ${CONFIG.discountPercent}% Off
        <small>Use code ${CONFIG.discountCode} at checkout. Expires soon.</small>
      </a>
      <p class="ps-cr-disclaimer">Limited time offer. Cannot be combined with other discounts.</p>
    `;

    // Close handlers
    const closeBtn = widget.querySelector('.ps-cr-close');
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      dismiss();
    });

    // Click outside to close (mobile overlay mode)
    onClickOutside(widget, overlay, dismiss);

    // CTA click tracking
    const cta = widget.querySelector('.ps-cr-cta');
    cta.addEventListener('click', () => {
      localStorage.setItem(CONFIG.lsKeyDismissed, Date.now().toString());
      isShowing = false;
    });

    return { overlay, widget };
  }

  function escapeHtml(str) {
    return str.replace(/[&<>"']/g, m => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[m]));
  }

  /* ── SHOW / DISMISS ────────────────────────────────────────── */
  function show() {
    if (isShowing) return;
    fetchCart().then(cart => {
      if (!cart.item_count || hasCheckout) return;

      // Don't show if user dismissed in last 24h
      const dismissedAt = localStorage.getItem(CONFIG.lsKeyDismissed);
      if (dismissedAt && (Date.now() - parseInt(dismissedAt, 10)) < 86400000) return;

      const { overlay, widget } = buildWidget(cart);
      document.body.appendChild(overlay);
      document.body.appendChild(widget);

      // Trigger animation
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          overlay.classList.add('active');
          widget.classList.add('active');
        });
      });

      isShowing = true;

      // Auto-dismiss after 60 seconds if ignored
      timerId = setTimeout(dismiss, 60000);
    });
  }

  function dismiss() {
    if (!isShowing) return;
    const widget = document.querySelector('.ps-cr-widget');
    const overlay = document.querySelector('.ps-cr-overlay');
    if (widget) {
      widget.classList.remove('active');
      setTimeout(() => widget.remove(), 450);
    }
    if (overlay) {
      overlay.classList.remove('active');
      setTimeout(() => overlay.remove(), 350);
    }
    if (timerId) { clearTimeout(timerId); timerId = null; }
    localStorage.setItem(CONFIG.lsKeyDismissed, Date.now().toString());
    isShowing = false;
  }

  /* ── TIMER LOGIC ───────────────────────────────────────────── */
  function startMonitoring() {
    stopMonitoring();

    // Listen for checkout page: if we land on /checkout or /checkouts, mark done
    if (/\/(checkout|checkouts)/.test(location.pathname)) {
      localStorage.removeItem(CONFIG.lsKeyCartTimestamp);
      hasCheckout = true;
      return;
    }

    pollId = setInterval(async () => {
      const cart = await fetchCart();

      if (!cart.item_count) {
        // Cart emptied — reset
        localStorage.removeItem(CONFIG.lsKeyCartTimestamp);
        if (isShowing) dismiss();
        return;
      }

      const stored = localStorage.getItem(CONFIG.lsKeyCartTimestamp);
      const now = Date.now();

      if (!stored) {
        // Cart just became non-empty; start the clock
        localStorage.setItem(CONFIG.lsKeyCartTimestamp, now.toString());
        return;
      }

      const elapsed = now - parseInt(stored, 10);
      const threshold = (window.PSCartRecovery && window.PSCartRecovery._DEMO_SPEED)
        ? CONFIG.demoSpeedMs
        : CONFIG.abandonThresholdMs;

      if (elapsed >= threshold && !isShowing) {
        show();
      }
    }, CONFIG.pollIntervalMs);
  }

  function stopMonitoring() {
    if (pollId) { clearInterval(pollId); pollId = null; }
  }

  /* ── INIT ──────────────────────────────────────────────────── */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }
    startMonitoring();
  }

  // Expose minimal API for demo page
  window.PSCartRecovery = {
    startMonitoring,
    stopMonitoring,
    show,
    dismiss,
    _DEMO_SPEED: false,
    _config: CONFIG
  };

  init();
})();
